import { isDoneLabel } from './status.js';
import type { Deliverable, Epic, ProductGoal } from './types.js';

export type NodeKind = 'goal' | 'epic' | 'deliverable' | 'standalone' | 'no-goal';
export type ViewMode = 'by-goal' | 'by-epic';

export interface Progress {
  done: number;
  total: number;
}

export interface TreeNode {
  /** Unique, stable key of this *occurrence* (the same Epic gets a different key under each Goal). */
  key: string;
  kind: NodeKind;
  /** monday item id for goal/epic/deliverable nodes; null for synthetic groups. */
  entityId: string | null;
  name: string;
  depth: number;
  url: string | null;
  /** Counts over the deliverables that pass the active filters. */
  progress: Progress;
  /** Counts over the complete, unfiltered dataset for this exact branch. */
  fullProgress: Progress;
  deliverable?: Deliverable;
  goal?: ProductGoal;
  epic?: Epic;
  /** Where a new Deliverable created from this node should be attached. */
  context: { productGoalId: string | null; epicId: string | null };
  children: TreeNode[];
}

export interface BuildTreeInput {
  productGoals: ProductGoal[];
  epics: Epic[];
  deliverables: Deliverable[];
  view: ViewMode;
  /** Names of statuses that count as done. */
  doneStatuses: string[];
  /** Predicate applied to deliverables. Omit for "no filtering". */
  matches?: (d: Deliverable) => boolean;
  /** When true, branches with no matching deliverable are hidden. */
  filtersActive?: boolean;
  /** Optional sibling ordering; defaults to board order. Product Goals always stay in target-date order. */
  sort?: { deliverable?: (a: Deliverable, b: Deliverable) => number; epic?: (a: Epic, b: Epic) => number };
}

export const KEY = {
  goal: (id: string) => `goal:${id}`,
  epic: (parent: string, id: string) => `${parent}/epic:${id}`,
  standalone: (parent: string) => `${parent}/standalone`,
  deliverable: (parent: string, id: string) => `${parent}/d:${id}`,
  noGoal: 'no-goal',
  rootStandalone: 'standalone',
} as const;

function progressOf(ds: Deliverable[], doneStatuses: string[]): Progress {
  const unique = new Map<string, Deliverable>();
  for (const d of ds) unique.set(d.id, d);
  let done = 0;
  for (const d of unique.values()) if (isDone(d, doneStatuses)) done++;
  return { done, total: unique.size };
}

export function isDone(d: Deliverable, doneStatuses: string[]): boolean {
  if (!d.status) return false;
  const set = doneStatuses.map((s) => s.toLowerCase());
  return set.includes(d.status.toLowerCase()) || (set.length === 0 && isDoneLabel(d.status));
}

function byPosition<T extends { position: number; name: string }>(a: T, b: T): number {
  return a.position - b.position || a.name.localeCompare(b.name);
}

export function compareGoals(a: ProductGoal, b: ProductGoal): number {
  // Earliest target date first; undated goals follow dated goals; ties keep board order.
  if (a.targetDate && b.targetDate) {
    if (a.targetDate !== b.targetDate) return a.targetDate < b.targetDate ? -1 : 1;
  } else if (a.targetDate) {
    return -1;
  } else if (b.targetDate) {
    return 1;
  }
  return byPosition(a, b);
}

interface Bucket {
  all: Deliverable[];
  shown: Deliverable[];
}

function makeNodes(
  parentKey: string,
  depth: number,
  ds: Deliverable[],
  ctx: TreeNode['context'],
  cmp: (a: Deliverable, b: Deliverable) => number,
): TreeNode[] {
  return [...ds].sort(cmp).map((d) => ({
    key: KEY.deliverable(parentKey, d.id),
    kind: 'deliverable' as const,
    entityId: d.id,
    name: d.name,
    depth,
    url: d.url,
    progress: { done: 0, total: 0 },
    fullProgress: { done: 0, total: 0 },
    deliverable: d,
    context: ctx,
    children: [],
  }));
}

export function buildTree(input: BuildTreeInput): TreeNode[] {
  const { productGoals, epics, deliverables, view, doneStatuses } = input;
  const matches = input.matches ?? (() => true);
  const filtersActive = input.filtersActive ?? false;
  const epicsById = new Map(epics.map((e) => [e.id, e]));
  const goalsById = new Map(productGoals.map((g) => [g.id, g]));
  const sortedEpics = [...epics].sort(input.sort?.epic ?? byPosition);
  const deliverableCmp = input.sort?.deliverable ?? byPosition;
  const makeDeliverableNodes = (parentKey: string, depth: number, ds: Deliverable[], ctx: TreeNode['context']): TreeNode[] => makeNodes(parentKey, depth, ds, ctx, deliverableCmp);

  const known = (ids: string[], map: Map<string, unknown>) => ids.filter((id) => map.has(id));
  const bucketFor = (ds: Deliverable[]): Bucket => ({ all: ds, shown: ds.filter(matches) });

  /** Epic children (and a standalone group) for a set of deliverables under `parentKey`. */
  function epicLevel(parentKey: string, depth: number, ds: Deliverable[], goalId: string | null): TreeNode[] {
    const perEpic = new Map<string, Deliverable[]>();
    const standalone: Deliverable[] = [];
    for (const d of ds) {
      const eids = known(d.epicIds, epicsById);
      if (eids.length === 0) standalone.push(d);
      for (const id of eids) {
        const list = perEpic.get(id) ?? [];
        list.push(d);
        perEpic.set(id, list);
      }
    }
    const nodes: TreeNode[] = [];
    for (const e of sortedEpics) {
      const all = perEpic.get(e.id);
      if (!all) continue;
      const b = bucketFor(all);
      if (filtersActive && b.shown.length === 0) continue;
      const key = KEY.epic(parentKey, e.id);
      const ctx = { productGoalId: goalId, epicId: e.id };
      nodes.push({
        key,
        kind: 'epic',
        entityId: e.id,
        name: e.name,
        depth,
        url: e.url,
        progress: progressOf(b.shown, doneStatuses),
        fullProgress: progressOf(b.all, doneStatuses),
        epic: e,
        context: ctx,
        children: makeDeliverableNodes(key, depth + 1, b.shown, ctx),
      });
    }
    if (standalone.length > 0) {
      const b = bucketFor(standalone);
      if (!(filtersActive && b.shown.length === 0)) {
        const key = KEY.standalone(parentKey);
        const ctx = { productGoalId: goalId, epicId: null };
        nodes.push({
          key,
          kind: 'standalone',
          entityId: null,
          name: 'Standalone Deliverables',
          depth,
          url: null,
          progress: progressOf(b.shown, doneStatuses),
          fullProgress: progressOf(b.all, doneStatuses),
          context: ctx,
          children: makeDeliverableNodes(key, depth + 1, b.shown, ctx),
        });
      }
    }
    return nodes;
  }

  if (view === 'by-epic') {
    const perEpic = new Map<string, Deliverable[]>();
    const standalone: Deliverable[] = [];
    for (const d of deliverables) {
      const eids = known(d.epicIds, epicsById);
      if (eids.length === 0) standalone.push(d);
      for (const id of eids) {
        const list = perEpic.get(id) ?? [];
        list.push(d);
        perEpic.set(id, list);
      }
    }
    const out: TreeNode[] = [];
    for (const e of sortedEpics) {
      const b = bucketFor(perEpic.get(e.id) ?? []);
      if (filtersActive && b.shown.length === 0) continue;
      const key = KEY.epic('', e.id).replace(/^\//, '');
      const ctx = { productGoalId: null, epicId: e.id };
      out.push({
        key,
        kind: 'epic',
        entityId: e.id,
        name: e.name,
        depth: 0,
        url: e.url,
        progress: progressOf(b.shown, doneStatuses),
        fullProgress: progressOf(b.all, doneStatuses),
        epic: e,
        context: ctx,
        children: makeDeliverableNodes(key, 1, b.shown, ctx),
      });
    }
    const b = bucketFor(standalone);
    if (b.all.length > 0 && !(filtersActive && b.shown.length === 0)) {
      const ctx = { productGoalId: null, epicId: null };
      out.push({
        key: KEY.rootStandalone,
        kind: 'standalone',
        entityId: null,
        name: 'Standalone Deliverables',
        depth: 0,
        url: null,
        progress: progressOf(b.shown, doneStatuses),
        fullProgress: progressOf(b.all, doneStatuses),
        context: ctx,
        children: makeDeliverableNodes(KEY.rootStandalone, 1, b.shown, ctx),
      });
    }
    return out;
  }

  // by-goal
  const perGoal = new Map<string, Deliverable[]>();
  const noGoal: Deliverable[] = [];
  for (const d of deliverables) {
    const gids = known(d.productGoalIds, goalsById);
    if (gids.length === 0) noGoal.push(d);
    for (const id of gids) {
      const list = perGoal.get(id) ?? [];
      list.push(d);
      perGoal.set(id, list);
    }
  }
  const out: TreeNode[] = [];
  for (const g of [...productGoals].sort(compareGoals)) {
    const b = bucketFor(perGoal.get(g.id) ?? []);
    // Product Goals always stay visible unless a filter hides them (empty goals stay when unfiltered).
    if (filtersActive && b.shown.length === 0) continue;
    const key = KEY.goal(g.id);
    out.push({
      key,
      kind: 'goal',
      entityId: g.id,
      name: g.name,
      depth: 0,
      url: g.url,
      progress: progressOf(b.shown, doneStatuses),
      fullProgress: progressOf(b.all, doneStatuses),
      goal: g,
      context: { productGoalId: g.id, epicId: null },
      children: epicLevel(key, 1, b.all, g.id),
    });
  }
  const nb = bucketFor(noGoal);
  if (nb.all.length > 0 && !(filtersActive && nb.shown.length === 0)) {
    out.push({
      key: KEY.noGoal,
      kind: 'no-goal',
      entityId: null,
      name: 'No Product Goal',
      depth: 0,
      url: null,
      progress: progressOf(nb.shown, doneStatuses),
      fullProgress: progressOf(nb.all, doneStatuses),
      context: { productGoalId: null, epicId: null },
      children: epicLevel(KEY.noGoal, 1, nb.all, null),
    });
  }
  return out;
}

export interface FlatRow {
  node: TreeNode;
  hasChildren: boolean;
  expanded: boolean;
  /** key of the parent row, null for roots */
  parentKey: string | null;
}

export function flattenVisible(roots: TreeNode[], expanded: ReadonlySet<string>): FlatRow[] {
  const rows: FlatRow[] = [];
  const walk = (nodes: TreeNode[], parentKey: string | null) => {
    for (const n of nodes) {
      const hasChildren = n.children.length > 0;
      const isOpen = hasChildren && expanded.has(n.key);
      rows.push({ node: n, hasChildren, expanded: isOpen, parentKey });
      if (isOpen) walk(n.children, n.key);
    }
  };
  walk(roots, null);
  return rows;
}

/** Keys of every node that has children (used by Expand All). */
export function expandableKeys(roots: TreeNode[]): string[] {
  const keys: string[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.children.length > 0) {
        keys.push(n.key);
        walk(n.children);
      }
    }
  };
  walk(roots);
  return keys;
}

/** Distinct underlying monday item ids reachable in the tree (proves nothing is duplicated). */
export function distinctDeliverableIds(roots: TreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.kind === 'deliverable' && n.entityId) ids.add(n.entityId);
      walk(n.children);
    }
  };
  walk(roots);
  return ids;
}
